import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  type CommentDraftDto,
  commentOnPost,
  draftComment,
  draftFeed,
  type EngagementFeedItemDto,
  getEngageConfig,
  getEngagementFeed,
  getEngagementHistory,
  markPostNotRelevant,
  reactToPost,
} from '../../lib/api.js';
import { formatRelative } from '../../lib/format.js';
import { LIVE_POLL_MS } from '../../lib/poll.js';
import { useWorkSummary } from '../../lib/use-work-summary.js';
import { AiEvidenceStrip } from '../ui/AiEvidenceStrip.js';
import { HistoryFacets } from '../ui/HistoryFacets.js';
import { JobChip } from '../ui/JobChip.js';
import { QueuePage, QueueScoreboard, type QueueTab } from './QueuePage.js';

/**
 * R19 — LinkedIn engagement ported onto QueuePage (was `LinkedinEngagementModal`,
 * deleted) — the template's hardest tenant. Routed `?view=engagement`: posts in
 * the list (verdict chips filter Ready/Caution/Hidden/All), and the canvas carries
 * the 6-reaction palette + a multi-line comment editor. Queue|History tabs;
 * caution vs noise is now the deterministic `suppression_class` (migration 0046),
 * with a reason-text fallback for pre-R19 drafts. Done-today scoreboard.
 */

const REACTIONS = [
  { key: 'like', emoji: '👍', label: 'Like' },
  { key: 'celebrate', emoji: '👏', label: 'Celebrate' },
  { key: 'support', emoji: '🤝', label: 'Support' },
  { key: 'love', emoji: '❤️', label: 'Love' },
  { key: 'insightful', emoji: '💡', label: 'Insightful' },
  { key: 'funny', emoji: '😄', label: 'Funny' },
] as const;

type Verdict = 'relevant' | 'ready' | 'caution' | 'hidden' | 'all';
const VERDICTS: ReadonlyArray<{ key: Verdict; label: string }> = [
  { key: 'relevant', label: 'Relevant' },
  { key: 'ready', label: 'Ready' },
  { key: 'caution', label: 'Caution' },
  { key: 'hidden', label: 'Hidden' },
  { key: 'all', label: 'All' },
];

function isCaution(d: CommentDraftDto | null | undefined): boolean {
  if (d?.status !== 'suppressed') return false;
  if (d.suppression_class) return d.suppression_class === 'caution';
  return Boolean(d.reason?.toLowerCase().includes('sensitive')); // pre-R19 fallback
}
function isNoise(d: CommentDraftDto | null | undefined): boolean {
  return d?.status === 'suppressed' && !isCaution(d);
}
function matchesVerdict(item: EngagementFeedItemDto, v: Verdict): boolean {
  const d = item.draft;
  switch (v) {
    case 'ready':
      return d?.status === 'ready';
    case 'caution':
      return isCaution(d);
    case 'hidden':
      return isNoise(d);
    case 'all':
      return true;
    default:
      return !isNoise(d); // 'relevant'
  }
}

export function LinkedinEngagementView() {
  const qc = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [tab, setTab] = useState<QueueTab>('queue');
  const [verdict, setVerdict] = useState<Verdict>('relevant');

  const feed = useQuery({
    queryKey: ['engagement-feed'],
    queryFn: ({ signal }) => getEngagementFeed({ signal }),
    enabled: tab === 'queue',
    refetchInterval: tab === 'queue' ? LIVE_POLL_MS : false,
  });
  const history = useQuery({
    queryKey: ['engagement-history'],
    queryFn: ({ signal }) => getEngagementHistory({ signal }),
    enabled: tab === 'history',
  });
  const cfg = useQuery({
    queryKey: ['engage-config'],
    queryFn: ({ signal }) => getEngageConfig({ signal }),
  });
  const work = useWorkSummary();
  const reactArmed = Boolean(cfg.data?.react_enabled);
  const commentArmed = Boolean(cfg.data?.comment_enabled);

  const allItems = feed.data?.items ?? [];
  const items = useMemo(
    () => allItems.filter((i) => i.social_id && matchesVerdict(i, verdict)),
    [allItems, verdict],
  );
  const undrafted = useMemo(
    () =>
      allItems.filter(
        (i) => i.social_id && i.draft?.status !== 'ready' && i.draft?.status !== 'suppressed',
      ),
    [allItems],
  );

  useEffect(() => {
    if (items.length === 0) {
      if (selectedId !== null) setSelectedId(null);
      return;
    }
    if (selectedId === null || !items.some((i) => i.id === selectedId)) {
      setSelectedId(items[0]?.id ?? null);
    }
  }, [items, selectedId]);

  const draftAll = useMutation({
    mutationFn: () => draftFeed(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['engagement-feed'] }),
  });

  return (
    <QueuePage<EngagementFeedItemDto>
      testId="engagement-view"
      title="LinkedIn engagement"
      count={items.length}
      items={items}
      getId={(i) => i.id}
      selectedId={selectedId}
      onSelect={setSelectedId}
      rowEstimate={76}
      isLoading={feed.isLoading && tab === 'queue'}
      isError={feed.isError}
      onRetry={() => void feed.refetch()}
      emptyTitle="Nothing to engage"
      emptyHint="The daily network refresh surfaces recent posts from your connections here."
      tab={tab}
      onTabChange={setTab}
      capabilityBadge={
        <span data-testid="eng-capability">
          {commentArmed ? 'comment armed' : 'react only'}
          {reactArmed ? '' : ' · reactions off'}
        </span>
      }
      filterChips={VERDICTS.map((v) => (
        <button
          key={v.key}
          type="button"
          className={verdict === v.key ? 'queue-chip active' : 'queue-chip'}
          aria-pressed={verdict === v.key}
          onClick={() => setVerdict(v.key)}
        >
          {v.label}
        </button>
      ))}
      bulkMenu={
        undrafted.length > 0 ? (
          draftAll.isPending ? (
            <JobChip label="Drafting feed…" testId="eng-draft-all-chip" />
          ) : (
            <>
              <button
                type="button"
                className="unified-overflow-item"
                data-testid="eng-draft-all"
                onClick={() => draftAll.mutate()}
              >
                <span>✨ Draft all ({undrafted.length})</span>
                <span className="unified-overflow-hint">
                  Queue an AI comment for each undrafted post
                </span>
              </button>
              {/* QA L11 — a failed bulk draft is no longer a silent no-op. */}
              {draftAll.isError ? (
                <div className="unified-overflow-hint" data-testid="eng-draft-all-error">
                  ⚠ Draft-all failed — try again.
                </div>
              ) : null}
            </>
          )
        ) : null
      }
      scoreboard={
        <QueueScoreboard
          openCount={allItems.filter((i) => i.draft?.status === 'ready').length}
          doneToday={work.data?.done_today?.engagement ?? 0}
        />
      }
      renderRow={(item) => <PostRowSummary item={item} />}
      renderCanvas={(item) =>
        item ? (
          <PostCanvas
            key={item.id}
            item={item}
            reactArmed={reactArmed}
            commentArmed={commentArmed}
          />
        ) : (
          <p className="queue-canvas-empty">Select a post to engage.</p>
        )
      }
      renderHistory={() => (
        <EngagementHistory rows={history.data?.items ?? []} loading={history.isLoading} />
      )}
    />
  );
}

function verdictBadge(item: EngagementFeedItemDto): { cls: string; label: string } {
  const d = item.draft;
  if (d?.status === 'ready') return { cls: 'ready', label: 'Ready' };
  if (isCaution(d)) return { cls: 'muted', label: '⚠ Caution' };
  if (isNoise(d)) return { cls: 'muted', label: 'Hidden' };
  if (d?.status === 'failed') return { cls: 'bad', label: 'Draft failed' };
  return { cls: 'pending', label: 'No draft' };
}

function PostRowSummary({ item }: { item: EngagementFeedItemDto }) {
  const v = verdictBadge(item);
  const snippet = (item.text ?? '').replace(/\s+/g, ' ').slice(0, 90);
  return (
    <span className="jc-row">
      <span className="jc-row-headline">{item.author_name ?? 'A connection'}</span>
      <span className="eng-row-snippet">{snippet || '(no text)'}</span>
      <span className="jc-row-meta">
        {item.in_network ? <span className="jc-kind">1st</span> : null}
        <span className={`jc-dot jc-dot-${v.cls}`}>{v.label}</span>
        <span>👍 {item.reaction_count ?? 0}</span>
        {item.posted_at ? <time>{formatRelative(item.posted_at)}</time> : null}
      </span>
    </span>
  );
}

function PostCanvas({
  item,
  reactArmed,
  commentArmed,
}: {
  item: EngagementFeedItemDto;
  reactArmed: boolean;
  commentArmed: boolean;
}) {
  const qc = useQueryClient();
  const ready = item.draft?.status === 'ready' ? (item.draft.text ?? '') : '';
  const [text, setText] = useState(ready);
  const [dirty, setDirty] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [reacted, setReacted] = useState<string | null>(null);
  const [commented, setCommented] = useState(false);
  const lastDraftAt = useRef<string | null>(item.draft?.created_at ?? null);
  const invalidate = () => qc.invalidateQueries({ queryKey: ['engagement-feed'] });
  const sid = item.social_id ?? '';
  const caution = isCaution(item.draft);

  // R18-21 review (HIGH) — when a freshly-drafted comment lands (a NEW comment_drafts
  // row → new created_at), seed the editor with it (unless the operator edited). The
  // canvas is keyed by post id, so without this the textarea never showed the AI draft.
  useEffect(() => {
    const d = item.draft;
    if (!d?.created_at || d.created_at === lastDraftAt.current) return;
    lastDraftAt.current = d.created_at;
    setDrafting(false);
    if (d.status === 'ready' && d.text && !dirty) setText(d.text);
  }, [item.draft, dirty]);

  const reactMut = useMutation({
    mutationFn: (rk: string) => reactToPost(sid, rk),
    onMutate: (rk: string) => {
      setErr(null);
      setReacted(rk);
    },
    onSuccess: invalidate,
    onError: (e) => {
      setReacted(null);
      setErr(e instanceof Error ? e.message : 'reaction failed');
    },
  });
  const commentMut = useMutation({
    mutationFn: () => commentOnPost(sid, text.trim()),
    onMutate: () => setErr(null),
    onSuccess: () => {
      setCommented(true);
      void invalidate();
    },
    onError: (e) => setErr(e instanceof Error ? e.message : 'comment failed'),
  });
  const draftMut = useMutation({
    mutationFn: (careful: boolean) => draftComment(sid, careful),
    onMutate: () => {
      setErr(null);
      setDirty(false); // an explicit (re)draft should re-seed even after an edit
      setDrafting(true);
    },
    onSuccess: invalidate,
    onError: (e) => {
      setDrafting(false);
      setErr(e instanceof Error ? e.message : 'draft failed');
    },
  });
  const notRelevantMut = useMutation({
    mutationFn: () => markPostNotRelevant(sid),
    onMutate: () => setErr(null),
    onSuccess: invalidate,
    // QA L11 — surface the failure instead of swallowing it (was a silent no-op).
    onError: (e) => setErr(e instanceof Error ? e.message : 'could not mark not-relevant'),
  });

  return (
    <div className="jc-canvas" data-testid="eng-canvas">
      <div className="jc-canvas-head">
        <h2>{item.author_name ?? 'A connection'}</h2>
        {item.share_url ? (
          <a className="jc-btn" href={item.share_url} target="_blank" rel="noreferrer">
            Open post
          </a>
        ) : null}
      </div>
      <p className="eng-post-text">{item.text ?? '(no text)'}</p>

      {/* R19 — the 6-reaction palette. */}
      {reactArmed ? (
        <div className="eng-reactions" data-testid="eng-reactions">
          {REACTIONS.map((r) => (
            <button
              key={r.key}
              type="button"
              className={reacted === r.key ? 'eng-react active' : 'eng-react'}
              title={r.label}
              aria-label={r.label}
              disabled={reactMut.isPending || reacted !== null}
              onClick={() => reactMut.mutate(r.key)}
            >
              {r.emoji}
            </button>
          ))}
          {reacted ? <span className="jc-sent">Reacted ✓</span> : null}
        </div>
      ) : null}

      {caution ? (
        <p className="jc-suppressed" data-testid="eng-caution">
          ⚠ Sensitive / regulatory — review carefully; no auto-comment.
        </p>
      ) : isNoise(item.draft) ? (
        <p className="jc-muted">AI judged this off-topic (hidden by default).</p>
      ) : null}

      {/* Multi-line comment editor (the single-line input is gone). */}
      {commentArmed ? (
        <>
          {item.draft?.status === 'ready' ? (
            <AiEvidenceStrip confidence={item.draft.confidence} factsUsed={item.draft.facts_used} />
          ) : null}
          <textarea
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              setDirty(true);
            }}
            rows={4}
            className="jc-draft"
            placeholder={drafting ? 'Drafting…' : 'Write a comment…'}
            aria-label="Comment"
          />
          <div className="jc-actions">
            <button
              type="button"
              className="jc-btn"
              onClick={() => draftMut.mutate(caution)}
              disabled={draftMut.isPending || drafting}
            >
              {drafting
                ? 'Drafting…'
                : caution
                  ? '✨ Draft carefully'
                  : item.draft?.status === 'ready'
                    ? '✨ Redraft'
                    : '✨ AI draft'}
            </button>
            {commented ? (
              <span className="jc-sent" data-testid="eng-commented">
                Commented ✓
              </span>
            ) : (
              <button
                type="button"
                className="jc-btn jc-send"
                data-testid="eng-comment-send"
                onClick={() => commentMut.mutate()}
                disabled={commentMut.isPending || !text.trim()}
              >
                {commentMut.isPending ? 'Posting…' : 'Comment'}
              </button>
            )}
            <button
              type="button"
              className="jc-btn"
              data-testid="eng-not-relevant"
              onClick={() => notRelevantMut.mutate()}
              disabled={notRelevantMut.isPending}
            >
              ✕ Not relevant
            </button>
          </div>
        </>
      ) : null}
      {err ? <p className="jc-failed">{err}</p> : null}
    </div>
  );
}

function EngagementHistory({
  rows,
  loading,
}: {
  rows: import('../../lib/api.js').EngagementHistoryItemDto[];
  loading: boolean;
}) {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<string | null>(null);
  const [action, setAction] = useState<string | null>(null);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter(
      (r) =>
        (!q || (r.author_name ?? '').toLowerCase().includes(q)) &&
        (!status || r.status === status) &&
        (!action || r.action === action),
    );
  }, [rows, search, status, action]);

  if (loading) return <p className="jc-muted">Loading…</p>;
  if (rows.length === 0) return <p className="jc-muted">No engagement yet.</p>;
  return (
    <>
      <HistoryFacets
        search={search}
        onSearch={setSearch}
        shown={filtered.length}
        total={rows.length}
        groups={[
          {
            label: 'Status',
            value: status,
            onChange: setStatus,
            options: [
              { value: 'sent', label: 'Sent' },
              { value: 'failed', label: 'Failed' },
            ],
          },
          {
            label: 'Type',
            value: action,
            onChange: setAction,
            options: [
              { value: 'post_comment', label: 'Comment' },
              { value: 'post_reaction', label: 'Reaction' },
            ],
          },
        ]}
      />
      {filtered.length === 0 ? (
        <p className="jc-muted">No matches.</p>
      ) : (
        <ul className="eng-history">
          {filtered.map((r) => (
            <li key={r.id}>
              <span className="jc-kind">
                {r.action === 'post_comment' ? 'Comment' : 'Reaction'}
              </span>
              <span
                className={`jc-dot jc-dot-${r.status === 'sent' ? 'ok' : r.status === 'failed' ? 'bad' : 'pending'}`}
              >
                {r.status}
              </span>
              <strong>{r.author_name ?? '—'}</strong>
              {r.payload ? <span className="eng-history-payload">{r.payload}</span> : null}
              {r.requested_at ? <time>{formatRelative(r.requested_at)}</time> : null}
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
